// components/DailyReport/ScheduleSelectionDialog.tsx
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    RadioGroup,
    Radio,
    FormControlLabel,
    CircularProgress,
    Divider,
    Paper
} from '@mui/material';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import { UnreportedScheduleItem } from '../../types/commonSchedule';
import { scheduleReportService } from '../../services/scheduleReportService';

interface ScheduleSelectionDialogProps {
    open: boolean;
    onClose: () => void;
    constructionId: number;
    constructionName: string;
    onScheduleSelect: (schedule: UnreportedScheduleItem | null) => void;
    initialSchedules?: UnreportedScheduleItem[]; // 初期データ用のプロパティを追加
}

export const ScheduleSelectionDialog: React.FC<ScheduleSelectionDialogProps> = ({
    open,
    onClose,
    constructionId,
    constructionName,
    onScheduleSelect,
    initialSchedules // 初期データ用のプロパティを追加
}) => {
    const [loading, setLoading] = useState(true);
    const [schedules, setSchedules] = useState<UnreportedScheduleItem[]>([]);
    const [selectedScheduleId, setSelectedScheduleId] = useState<number | null>(null);

    useEffect(() => {
        if (!open || !constructionId) return;
    
        // 初期データが渡されている場合はそれを使用
        if (initialSchedules && initialSchedules.length > 0) {
            setSchedules(initialSchedules);
            setLoading(false);
            return;
        }
    
        // 初期データがない場合のみAPIを呼び出し
        const fetchSchedules = async () => {
            setLoading(true);
            try {
                const result = await scheduleReportService.getUnreportedSchedules(constructionId);
                setSchedules(result.schedules || []);
            } catch (error) {
                console.error('未報告スケジュール取得エラー:', error);
            } finally {
                setLoading(false);
            }
        };
    
        fetchSchedules();
    }, [open, constructionId, initialSchedules]); // 依存配列に initialSchedules を追加
    

    const handleScheduleSelect = () => {
        const selectedSchedule = schedules.find(s => s.id === selectedScheduleId) || null;
        onScheduleSelect(selectedSchedule);
    };

    const handleNoSchedule = () => {
        onScheduleSelect(null);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle sx={{
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'primary.light',
                color: 'primary.contrastText'
            }}>
                日報登録 - スケジュール選択
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {/* 工事情報表示 */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" fontWeight="bold">工事:</Typography>
                    <Typography variant="body1">{constructionName}</Typography>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    今日を含む過去の日報未登録スケジュール:
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                        <CircularProgress />
                    </Box>
                ) : schedules.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 2, textAlign: 'center' }}>
                        未登録のスケジュールがありません
                    </Typography>
                ) : (
                    <RadioGroup
                        value={selectedScheduleId || ''}
                        onChange={(e) => setSelectedScheduleId(Number(e.target.value))}
                    >
                        {schedules.map((schedule) => (
                            <Paper
                                key={schedule.id}
                                variant="outlined"
                                sx={{
                                    mb: 2,
                                    p: 0,
                                    overflow: 'hidden',
                                    borderRadius: 1,
                                    cursor: 'pointer',
                                    '&:hover': {
                                        bgcolor: 'action.hover'
                                    }
                                }}
                                onClick={() => setSelectedScheduleId(schedule.id)}
                            >
                                <FormControlLabel
                                    value={schedule.id}
                                    control={<Radio />}
                                    label=""
                                    sx={{
                                        m: 0,
                                        p: 2,
                                        width: '100%',
                                        '& .MuiFormControlLabel-label': {
                                            width: '100%'
                                        }
                                    }}
                                />
                                <Box sx={{ pl: 6, pr: 2, pb: 2, mt: -4 }}>
                                    <Typography variant="subtitle1" component="div">
                                        {format(new Date(schedule.scheduleDate), 'M/d (EEE)', { locale: ja })}
                                        {' '}
                                        {schedule.teamType === 'equipment' ? '設備班' : '仮設班'}
                                    </Typography>

                                    {/* メンバー情報 */}
                                    {schedule.memberData && schedule.memberData.length > 0 && (
                                        <Box sx={{ mt: 1 }}>
                                            <Typography variant="body2" component="div">
                                                <strong>メンバー:</strong> {schedule.memberData.map(m =>
                                                    m.displayName || m.LastName || m.FormattedVendorMemberName || m.VendorMemberName
                                                ).join(', ')}
                                            </Typography>
                                        </Box>
                                    )}

                                    {/* 車両情報 */}
                                    {schedule.vehicleData && schedule.vehicleData.length > 0 && (
                                        <Box sx={{ mt: 0.5 }}>
                                            <Typography variant="body2" component="div">
                                                <strong>車両:</strong> {schedule.vehicleData.map(v =>
                                                    v.Car_Nickname || v.Car_Model || `車両ID: ${v.Car_ID}`
                                                ).join(', ')}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </Paper>
                        ))}
                    </RadioGroup>
                )}

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                    ※ スケジュールなしで日報作成する場合は「スケジュールなしで作成」を選択
                </Typography>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Button
                    variant="outlined"
                    onClick={handleNoSchedule}
                    sx={{ mr: 1 }}
                >
                    スケジュールなしで作成
                </Button>
                <Button
                    variant="contained"
                    onClick={handleScheduleSelect}
                    disabled={selectedScheduleId === null && schedules.length > 0}
                >
                    選択して次へ
                </Button>
            </DialogActions>
        </Dialog>
    );
};